import DOMPurify from 'dompurify';

/**
 * Configuration for HTML sanitization
 * Allows only safe HTML tags and attributes for rich text content
 */
const SANITIZE_CONFIG: DOMPurify.Config = {
  // Allow only safe HTML tags for rich text formatting
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'span', 'div', 'img', 'hr'
  ],
  
  // Allow only safe attributes
  ALLOWED_ATTR: [
    'class', 'id', 'style', 'src', 'alt', 'width', 'height', 'loading'
  ],
  
  // Remove any dangerous attributes
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
    'onchange', 'onsubmit', 'onreset', 'onselect', 'onunload'
  ],
  
  // Remove any dangerous tags
  FORBID_TAGS: [
    'script', 'object', 'embed', 'applet', 'form', 'input', 'button',
    'select', 'textarea', 'iframe', 'frame', 'frameset'
  ],
  
  // Keep relative URLs safe
  ALLOW_DATA_ATTR: false,
  
  // Remove any javascript: URLs
  ALLOW_UNKNOWN_PROTOCOLS: false,
  
  // Sanitize CSS in style attributes
  SANITIZE_DOM: true,
  
  // Return DOM instead of string for better performance
  RETURN_DOM: false,
  
  // Keep document structure
  KEEP_CONTENT: true
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  try {
    // Use DOMPurify to sanitize the HTML
    const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG as any);
    return sanitized as unknown as string;
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    // Return empty string if sanitization fails
    return '';
  }
};

/**
 * Sanitizes HTML content with custom configuration
 * @param html - HTML string to sanitize
 * @param config - Custom DOMPurify configuration
 * @returns Sanitized HTML string
 */
export const sanitizeHtmlWithConfig = (html: string, config: DOMPurify.Config): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  try {
    // Merge custom config with default config
    const mergedConfig = { ...SANITIZE_CONFIG, ...config };
    const sanitized = DOMPurify.sanitize(html, mergedConfig as any);
    return sanitized as unknown as string;
  } catch (error) {
    console.error('Error sanitizing HTML with custom config:', error);
    // Return empty string if sanitization fails
    return '';
  }
};

/**
 * Checks if HTML content contains potentially dangerous elements
 * @param html - HTML string to check
 * @returns True if content is safe, false if potentially dangerous
 */
export const isHtmlSafe = (html: string): boolean => {
  if (!html || typeof html !== 'string') {
    return true;
  }
  
  try {
    // Sanitize and compare with original
    const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG as any) as unknown as string;
    return sanitized === html;
  } catch (error) {
    console.error('Error checking HTML safety:', error);
    return false;
  }
};

/**
 * Removes all HTML tags from content, leaving only text
 * @param html - HTML string to strip
 * @returns Plain text content
 */
export const stripHtmlTags = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  try {
    // Use DOMPurify to strip all tags
    const stripped = DOMPurify.sanitize(html, { 
      ALLOWED_TAGS: [], 
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true 
    });
    return stripped;
  } catch (error) {
    console.error('Error stripping HTML tags:', error);
    return html;
  }
};

/**
 * Validates that HTML content only contains allowed rich text elements
 * @param html - HTML string to validate
 * @returns True if content only contains allowed elements
 */
export const validateRichTextHtml = (html: string): boolean => {
  if (!html || typeof html !== 'string') {
    return true;
  }
  
  try {
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Check for forbidden tags
    const forbiddenTags = SANITIZE_CONFIG.FORBID_TAGS || [];
    for (const tag of forbiddenTags) {
      if (tempDiv.querySelector(tag)) {
        return false;
      }
    }
    
    // Check for forbidden attributes
    const forbiddenAttrs = SANITIZE_CONFIG.FORBID_ATTR || [];
    const allElements = tempDiv.querySelectorAll('*');
    for (const element of allElements) {
      for (const attr of forbiddenAttrs) {
        if (element.hasAttribute(attr)) {
          return false;
        }
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error validating rich text HTML:', error);
    return false;
  }
};
